import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const cls = (...c) => c.filter(Boolean).join(" ");

function readUTM() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source"),
    utm_medium: p.get("utm_medium"),
    utm_campaign: p.get("utm_campaign"),
  };
}

const LOCATION_OPTIONS_STATIC = ["Online / Remote", "Other"];

// Convert "HH:MM" 24h → "h:mm AM/PM"
function fmt12hEmbed(hhmm) {
  if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return hhmm || "";
  const [hStr, mStr] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

// Core class programs offered at all branches.
const CORE_PROGRAMS_EMBED = [
  { code: "STRENGTH", label: "Strength" },
  { code: "CONDITIONING", label: "Conditioning" },
  { code: "MMA", label: "MMA" },
  { code: "HYROX", label: "Hyrox" },
  { code: "OPEN_GYM", label: "Open Gym" },
];

export default function LeadFormEmbed() {
  const [config, setConfig] = useState(null);
  const [slots, setSlots] = useState([]);
  const [f, setF] = useState({
    athlete_profile: "", athlete_profile_other: "",
    heard_from: "", heard_from_other: "",
    first_name: "", last_name: "", email: "", phone: "",
    programme_code: "", programme_label: "", programme_other: "",
    preferred_start_date: "",
    location_choice: "",  // one of: branch_id | "online" | "other"
    location_other: "",
    is_online: false,
    branch_id: "",
    preferred_time_slot: "",
    notes: "",
    referred_by_phone: "",
    website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const utm = useMemo(() => readUTM(), []);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/lead-form-config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const isValidBranch = f.branch_id && f.branch_id !== "online" && f.branch_id !== "other";
  const showTimeSlot = isValidBranch && !f.is_online;

  // Fetch time slots when Cunningham + programme selected
  useEffect(() => {
    if (!showTimeSlot) { setSlots([]); return; }
    const params = new URLSearchParams({ branch_id: f.branch_id });
    if (f.programme_code) params.set("session_type", f.programme_code);
    axios.get(`${API_URL}/api/public/time-slots?${params.toString()}`)
      .then((r) => setSlots(r.data)).catch(() => setSlots([]));
  }, [showTimeSlot, f.branch_id, f.programme_code]);

  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const onLocationChange = (val) => {
    if (val === "online") {
      setF((x) => ({ ...x, location_choice: val, is_online: true, branch_id: "", preferred_time_slot: "" }));
    } else if (val === "other") {
      setF((x) => ({ ...x, location_choice: val, is_online: false, branch_id: "", preferred_time_slot: "" }));
    } else {
      // val is branch_id
      setF((x) => ({ ...x, location_choice: val, is_online: false, branch_id: val, preferred_time_slot: "" }));
    }
  };

  const onProgrammeChange = (code, label) => {
    setF((x) => ({ ...x, programme_code: code, programme_label: label, preferred_time_slot: "" }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setErr("");
    const phoneDigits = f.phone.replace(/\D/g, "");
    if (!f.first_name.trim() || !f.last_name.trim()) { setErr("First and Last name are required."); return; }
    if (phoneDigits.length < 10) { setErr("Please enter a valid 10-digit phone number."); return; }
    if (!f.email.trim() || !/^\S+@\S+\.\S+$/.test(f.email)) { setErr("Please enter a valid email address."); return; }
    if (!f.preferred_start_date) { setErr("Please pick a preferred training start date."); return; }
    if (!f.location_choice) { setErr("Please pick your training location."); return; }
    if (f.location_choice === "other" && !f.location_other.trim()) { setErr("Tell us your preferred location."); return; }
    if (f.athlete_profile === "Other" && !f.athlete_profile_other.trim()) { setErr("Tell us what describes you."); return; }
    if (f.heard_from === "Other" && !f.heard_from_other.trim()) { setErr("Please specify where you heard about us."); return; }
    if (f.referred_by_phone) {
      const rd = f.referred_by_phone.replace(/\D/g, "");
      if (rd.length !== 10) { setErr("Referrer phone should be 10 digits."); return; }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/leads`, {
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        email: f.email.trim(),
        phone: f.phone.trim(),
        athlete_profile: f.athlete_profile || null,
        athlete_profile_other: f.athlete_profile === "Other" ? f.athlete_profile_other.trim() : null,
        heard_from: f.heard_from || null,
        heard_from_other: f.heard_from === "Other" ? f.heard_from_other.trim() : null,
        programme_code: f.programme_code || null,
        programme_label: f.programme_label || null,
        programme_other: null,
        preferred_start_date: f.preferred_start_date,
        branch_id: f.is_online || f.location_choice === "other" ? null : f.branch_id,
        location_other: f.location_choice === "other" ? f.location_other.trim() : null,
        is_online: f.is_online,
        preferred_time_slot: showTimeSlot ? (f.preferred_time_slot || null) : null,
        referred_by_phone: f.referred_by_phone.trim() || null,
        notes: f.notes.trim() || null,
        ...utm,
      });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center font-sans text-sm text-zinc-500">Loading…</div>;
  }

  if (done) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="lead-embed-success">
        <div className="max-w-md w-full bg-zinc-950 border border-volt/40 rounded-lg p-8 text-center">
          <div className="text-5xl mb-4">🎉</div>
          <div className="font-display text-3xl font-black uppercase tracking-tight mb-2">Thank you!</div>
          <div className="text-zinc-400 text-sm">We will get in touch with you shortly.</div>
          <a href="https://nammaxfit.com" target="_top" data-testid="explore-btn"
             className="inline-block mt-6 bg-volt hover:bg-volt/90 text-black font-bold uppercase tracking-[0.16em] text-xs px-5 py-3 rounded-sm">
            Explore Training Programmes
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-6 sm:p-10 font-sans" data-testid="lead-embed-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.28em] text-volt font-bold">NAMMAxFit</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight mt-1">Reserve Your Spot</h1>
          <p className="text-zinc-500 text-sm mt-2">Help us understand your goals so we can recommend the right coach, location and programme.</p>
        </div>

        <form onSubmit={submit} noValidate className="space-y-8" data-testid="lead-form">
          {/* Honeypot */}
          <input type="text" value={f.website} onChange={(e) => upd("website", e.target.value)} name="website" tabIndex={-1} autoComplete="off"
            style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} aria-hidden="true" />

          {/* SECTION 1 */}
          <Section title="What best describes you?" data-testid="section-athlete">
            <Dropdown value={f.athlete_profile} onChange={(v) => upd("athlete_profile", v)}
              options={config.athlete_profiles.map((o) => ({ value: o, label: o }))}
              placeholder="— select —" testid="athlete-select" />
            {f.athlete_profile === "Other" && (
              <TextInput placeholder="What describes you?" value={f.athlete_profile_other}
                onChange={(v) => upd("athlete_profile_other", v)} testid="athlete-other" className="mt-3" />
            )}
          </Section>

          {/* SECTION 2 */}
          <Section title="Where did you hear about NAMMA?" data-testid="section-heard">
            <Dropdown value={f.heard_from} onChange={(v) => upd("heard_from", v)}
              options={config.heard_from_options.map((o) => ({ value: o, label: o }))}
              placeholder="— select —" testid="heard-select" />
            {f.heard_from === "Other" && (
              <TextInput placeholder="Please specify" value={f.heard_from_other}
                onChange={(v) => upd("heard_from_other", v)} testid="heard-other" className="mt-3" />
            )}
          </Section>

          {/* SECTION 3 */}
          <Section title="Personal Details" required data-testid="section-personal">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="First Name" required>
                <TextInput value={f.first_name} onChange={(v) => upd("first_name", v)} testid="lf-first-name" />
              </Field>
              <Field label="Last Name" required>
                <TextInput value={f.last_name} onChange={(v) => upd("last_name", v)} testid="lf-last-name" />
              </Field>
              <Field label="Email Address" required>
                <TextInput type="email" value={f.email} onChange={(v) => upd("email", v)} testid="lf-email" />
              </Field>
              <Field label="Phone Number" required>
                <TextInput type="tel" value={f.phone} onChange={(v) => upd("phone", v)} placeholder="+91 98XXXXXXXX" testid="lf-phone" />
              </Field>
            </div>
          </Section>

          {/* SECTION 4 — REMOVED. "Preferred Program" now lives right after Location (branch) selection. */}

          {/* SECTION 5 */}
          <Section title="Preferred Training Start Date" required data-testid="section-startdate">
            <input type="date" value={f.preferred_start_date}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => upd("preferred_start_date", e.target.value)}
              data-testid="lf-startdate"
              className="w-full sm:w-64 bg-zinc-950 border border-zinc-800 focus:border-volt focus:outline-none rounded-sm px-3 py-2.5 text-sm" />
          </Section>

          {/* SECTION 6 */}
          <Section title="Which training location are you interested in?" required data-testid="section-location">
            <Dropdown value={f.location_choice} onChange={onLocationChange}
              options={[
                ...config.branches.map((b) => ({ value: b.id, label: b.name })),
                { value: "online", label: "Online / Remote" },
                { value: "other", label: "Other" },
              ]}
              placeholder="— select a location —" testid="loc-select" />
            {f.location_choice === "other" && (
              <TextInput placeholder="Preferred Location" value={f.location_other}
                onChange={(v) => upd("location_other", v)} testid="loc-other" className="mt-3" />
            )}
          </Section>

          {/* SECTION 6a — Preferred Program (right after Location) */}
          {isValidBranch && (
            <Section title="Preferred Program" data-testid="section-preferred-program">
              <Dropdown value={f.programme_code}
                onChange={(v) => {
                  const p = CORE_PROGRAMS_EMBED.find((x) => x.code === v);
                  onProgrammeChange(v, p?.label || v);
                }}
                options={CORE_PROGRAMS_EMBED.map((p) => ({ value: p.code, label: p.label }))}
                placeholder="— select a program —" testid="preferred-program-select" />
            </Section>
          )}

          {/* SECTION 6b — Preferred Timeslot (conditional on program) */}
          {showTimeSlot && f.programme_code && (
            <Section title="Preferred Timeslot" data-testid="section-preferred-timeslot">
              <div className="text-xs text-zinc-500 mb-3">Times below are pulled live from our class schedule for the location and program you picked.</div>
              {(() => {
                let timeOpts = [];
                if (f.programme_code === "OPEN_GYM") {
                  const s = slots[0];
                  if (s) {
                    const range = s.start && s.end ? `${fmt12hEmbed(s.start)} – ${fmt12hEmbed(s.end)}` : "";
                    timeOpts = [{ value: s.label || "OPEN_GYM_FLEX",
                                  label: `Anytime (walk-in)${range ? ` · ${range}` : ""}` }];
                  }
                } else {
                  const uniq = Array.from(new Set(slots.map((s) => s.start).filter(Boolean))).sort();
                  timeOpts = uniq.map((t) => ({ value: t, label: fmt12hEmbed(t) }));
                }
                if (!timeOpts.length) {
                  return <div className="text-zinc-600 text-sm" data-testid="no-timeslots">No live schedule matches — a coach will confirm your slot when they reach out.</div>;
                }
                return (
                  <Dropdown value={f.preferred_time_slot} onChange={(v) => upd("preferred_time_slot", v)}
                    options={timeOpts}
                    placeholder="— pick a time —" testid="preferred-timeslot-select" />
                );
              })()}
            </Section>
          )}

          {/* REFERRER (kept from prev version — optional) */}
          <Section title="Referred by a member?" data-testid="section-referral">
            <div className="text-xs text-zinc-500 mb-2">Optional. If a friend/member told you about us, enter their 10-digit phone so we can thank them.</div>
            <TextInput value={f.referred_by_phone} onChange={(v) => upd("referred_by_phone", v)}
              placeholder="9XXXXXXXXX" testid="lf-referred" className="sm:w-64" />
          </Section>

          {/* FINAL */}
          <Section title="Anything else you'd like us to know?" data-testid="section-notes">
            <textarea value={f.notes} onChange={(e) => upd("notes", e.target.value)} rows={3}
              placeholder="Tell us about your goals, previous training experience, injuries, or anything you'd like your coach to know."
              data-testid="lf-notes"
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-volt focus:outline-none rounded-sm px-3 py-2.5 text-sm resize-none" />
          </Section>

          {err && <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-sm p-3" data-testid="lf-error">{err}</div>}

          <button type="submit" disabled={submitting} data-testid="lf-submit"
            className={cls(
              "w-full bg-volt hover:bg-volt/90 text-black font-bold uppercase tracking-[0.18em] py-3.5 rounded-sm transition-all text-base",
              submitting && "opacity-60 cursor-not-allowed",
            )}>
            {submitting ? "Sending…" : "Reserve My Spot"}
          </button>

          <div className="text-[10px] text-zinc-600 text-center pt-2">
            We only use your details to contact you about your trial. No spam.
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------- Reusable sub-components ---------- */

function Section({ title, required, children, ...rest }) {
  return (
    <section className="pb-6 border-b border-zinc-900" {...rest}>
      <h2 className="text-base font-bold uppercase tracking-[0.08em] mb-4">
        {title}{required && <span className="text-volt ml-1">*</span>}
      </h2>
      {children}
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">
        {label}{required && <span className="text-volt ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}

function TextInput({ value, onChange, placeholder, type = "text", testid, className = "" }) {
  return (
    <input type={type} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className={cls(
        "w-full bg-zinc-950 border border-zinc-800 focus:border-volt focus:outline-none rounded-sm px-3 py-2.5 text-sm",
        className,
      )} />
  );
}

function Dropdown({ value, onChange, options, placeholder, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-zinc-950 border border-zinc-800 focus:border-volt focus:outline-none rounded-sm px-3 py-2.5 text-sm appearance-none cursor-pointer"
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ccff00' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: 36 }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

function RadioGroup({ name, value, onChange, options, testidPrefix }) {
  const items = options.map((o) => typeof o === "string" ? { value: o, label: o } : o);
  return (
    <div className="space-y-2">
      {items.map((opt) => (
        <label key={opt.value}
          className={cls(
            "flex items-start gap-3 p-3 border rounded-sm cursor-pointer transition-all",
            value === opt.value
              ? "border-volt bg-volt/5"
              : "border-zinc-800 hover:border-zinc-700 bg-zinc-950",
          )}
          data-testid={`${testidPrefix}-${opt.value.toString().replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        >
          <input type="radio" name={name} value={opt.value} checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="mt-1 accent-volt" />
          <span className="text-sm text-zinc-200">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}
